import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowUp } from 'lucide-react';
import {
  getBackToTopBehavior,
  shouldShowBackToTop,
} from './backToTop.js';

export default function BackToTop() {
  const [visible, setVisible] = useState(false);
  const frame = useRef(null);
  const scrollElement = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const updateVisibility = () => {
      if (frame.current !== null) return;
      frame.current = window.requestAnimationFrame(() => {
        frame.current = null;
        const root = document.scrollingElement;
        const element = scrollElement.current || root;

        const scrollTop = element === root
          ? window.scrollY || root?.scrollTop || 0
          : element?.scrollTop || 0;
        const viewportHeight = element === root
          ? window.innerHeight
          : element?.clientHeight || window.innerHeight;
        setVisible(shouldShowBackToTop(scrollTop, viewportHeight));
      });
    };
    const handleInnerScroll = (event) => {
      if (event.target instanceof HTMLElement
        && event.target !== document.documentElement
        && event.target !== document.body) {
        scrollElement.current = event.target;
      }
      updateVisibility();
    };
    const handleWindowScroll = () => {
      scrollElement.current = document.scrollingElement;
      updateVisibility();
    };

    scrollElement.current = document.scrollingElement;
    document.addEventListener('scroll', handleInnerScroll, { passive: true, capture: true });
    window.addEventListener('scroll', handleWindowScroll, { passive: true });
    window.addEventListener('resize', updateVisibility, { passive: true });
    updateVisibility();

    return () => {
      document.removeEventListener('scroll', handleInnerScroll, true);
      window.removeEventListener('scroll', handleWindowScroll);
      window.removeEventListener('resize', updateVisibility);
      if (frame.current !== null) {
        window.cancelAnimationFrame(frame.current);
        frame.current = null;
      }
    };
  }, []);

  useEffect(() => {
    scrollElement.current = document.scrollingElement;
    const behavior = getBackToTopBehavior(
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    );
    const options = { top: 0, left: 0, behavior };
    window.scrollTo(options);

    const root = document.scrollingElement;
    if (root) root.scrollTo(options);
    if (scrollElement.current && scrollElement.current !== root && scrollElement.current.isConnected) {
      scrollElement.current.scrollTo(options);
    }
    document.querySelectorAll('.admin-content, [data-scroll-container]').forEach((element) => {
      element.scrollTo(options);
    });
  }, [location.pathname, location.search, location.hash]);

  const scrollToTop = () => {
    const behavior = getBackToTopBehavior(
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    );
    const options = { top: 0, left: 0, behavior };
    window.scrollTo(options);
    const root = document.scrollingElement;
    if (root) root.scrollTo(options);
    if (scrollElement.current && scrollElement.current !== root) {
      scrollElement.current.scrollTo(options);
    }
  };

  return (
    <button
      type="button"
      className={`back-to-top${visible ? ' is-visible' : ''}`}
      onClick={scrollToTop}
      aria-label="بازگشت به بالای صفحه"
      aria-hidden={!visible}
      tabIndex={visible ? 0 : -1}
      disabled={!visible}
    >
      <ArrowUp size={20} aria-hidden="true" />
    </button>
  );
}
